// frontend/src/components/Portfolio/ClimateRiskBreakdown.tsx
// Enhanced with: Interactive Bubble Chart, Properties Affected, Smart Value Formatting

import React, { useState, useMemo, useCallback } from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip as RechartsTooltip,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  Treemap,
  Legend,
} from 'recharts';

const ALL_HAZARDS = [
  'avalanche', 'flood', 'flood_r', 'cold', 'drought', 'earthquake',
  'hail', 'heat', 'hurricane', 'ice', 'landslide', 'lightning',
  'tornado', 'tsunami', 'volcanic', 'wildfire', 'wind', 'winter'
];

const HAZARD_LABELS: { [key: string]: string } = {
  avalanche: 'Avalanche',
  flood: 'Coastal Flood',
  flood_r: 'River Flood',
  cold: 'Cold Wave',
  drought: 'Drought',
  earthquake: 'Earthquake',
  hail: 'Hail',
  heat: 'Heat Wave',
  hurricane: 'Hurricane',
  ice: 'Ice Storm',
  landslide: 'Landslide',
  lightning: 'Lightning',
  tornado: 'Tornado',
  tsunami: 'Tsunami',
  volcanic: 'Volcanic Activity',
  wildfire: 'Wildfire',
  wind: 'Strong Wind',
  winter: 'Winter Storm'
};

const COLORS: { [key: string]: string } = {
  avalanche: '#9E9E9E',
  flood: '#2196F3',
  flood_r: '#03A9F4',
  cold: '#00BCD4',
  drought: '#FFC107',
  earthquake: '#795548',
  hail: '#607D8B',
  heat: '#FF9800',
  hurricane: '#9C27B0',
  ice: '#00E5FF',
  landslide: '#8BC34A',
  lightning: '#FFEB3B',
  tornado: '#FF5722',
  tsunami: '#00BCD4',
  volcanic: '#F44336',
  wildfire: '#FF6F00',
  wind: '#9E9E9E',
  winter: '#B3E5FC',
  primary: '#2563EB',
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#10B981'
};

const normalizeAssetValue = (value: any): number => {
  if (typeof value === 'string') {
    const cleaned = value.replace(/[$,]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  return typeof value === 'number' ? value : 0;
};

const getRiskScoreNumber = (score: any): number => {
  if (typeof score === 'string') {
    const parsed = parseFloat(score);
    return isNaN(parsed) ? 0 : parsed;
  }
  return typeof score === 'number' ? score : 0;
};

// Smart value formatting - $K, $M, $B
const formatSmartValue = (value: number): string => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

// Full value formatting for tooltips
const formatFullValue = (value: number): string => {
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 
  }).format(value);
};

const getHazardEAL = (item: any, hazard: string): number => {
  const assetValue = normalizeAssetValue(item.asset_value);
  const hazardScore = getRiskScoreNumber(item[hazard]);
  return (assetValue * hazardScore) / 100;
};

// Custom Bubble Tooltip
const CustomBubbleTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper sx={{ p: 2, maxWidth: 280, boxShadow: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: data.color }} />
          <Typography variant="subtitle1" fontWeight={600}>{data.name}</Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'grid', gap: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Properties Affected:</Typography>
            <Typography variant="body2" fontWeight={600}>{data.count.toLocaleString()}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Value at Risk:</Typography>
            <Typography variant="body2" fontWeight={600}>{formatFullValue(data.assetValue)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Avg Risk Score:</Typography>
            <Typography variant="body2" fontWeight={600}>{data.avgRisk.toFixed(1)}/10</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Est. Annual Loss:</Typography>
            <Typography variant="body2" fontWeight={600} color="error.main">
              {formatSmartValue(data.totalAnnualLoss)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">% of Portfolio:</Typography>
            <Typography variant="body2" fontWeight={600}>{data.portfolioPercentage.toFixed(1)}%</Typography>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Click for detailed breakdown
        </Typography>
      </Paper>
    );
  }
  return null;
};

// Custom Treemap Content
const CustomTreemapContent = (props: any) => {
  const { x, y, width, height, name, count, assetValue, color } = props;
  
  if (width < 40 || height < 35) return null;
  
  // Calculate font sizes based on cell size
  const nameFontSize = Math.min(15, Math.max(11, width / 7));
  const detailFontSize = Math.min(13, Math.max(10, width / 9));
  
  // Format value with appropriate decimals
  const formatValue = (val: number): string => {
    if (!val || isNaN(val)) return '$0';
    if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
    if (val >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
    if (val >= 1e3) return `$${(val / 1e3).toFixed(2)}K`;
    return `$${val.toFixed(2)}`;
  };
  
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={color}
        stroke="#fff"
        strokeWidth={2}
        rx={4}
        style={{ cursor: 'pointer' }}
      />
      {/* Dark overlay for better text contrast */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="rgba(0,0,0,0.35)"
        rx={4}
      />
      {width > 50 && height > 45 && (
        <>
          <text
            x={x + width / 2}
            y={y + height / 2 - 12}
            textAnchor="middle"
            fill="#fff"
            fontSize={nameFontSize}
            fontWeight={600}
            fontFamily="system-ui, -apple-system, sans-serif"
            stroke="rgba(0,0,0,0.5)"
            strokeWidth={0.5}
            paintOrder="stroke"
          >
            {name}
          </text>
          <text
            x={x + width / 2}
            y={y + height / 2 + 6}
            textAnchor="middle"
            fill="#fff"
            fontSize={detailFontSize}
            fontWeight={500}
            fontFamily="system-ui, -apple-system, sans-serif"
            stroke="rgba(0,0,0,0.4)"
            strokeWidth={0.3}
            paintOrder="stroke"
          >
            {count} locations
          </text>
          {height > 60 && (
            <text
              x={x + width / 2}
              y={y + height / 2 + 24}
              textAnchor="middle"
              fill="#fff"
              fontSize={detailFontSize}
              fontWeight={500}
              fontFamily="system-ui, -apple-system, sans-serif"
              stroke="rgba(0,0,0,0.4)"
              strokeWidth={0.3}
              paintOrder="stroke"
            >
              {formatValue(assetValue)}
            </text>
          )}
        </>
      )}
    </g>
  );
};

interface ClimateRiskBreakdownProps {
  portfolio: any;
}

export default function ClimateRiskBreakdown({ portfolio }: ClimateRiskBreakdownProps) {
  const [showAllHazards, setShowAllHazards] = useState(false);
  const [filterMode, setFilterMode] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [chartType, setChartType] = useState<'bubble' | 'treemap'>('bubble');
  const [selectedHazard, setSelectedHazard] = useState<any | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const getRiskThreshold = () => {
    switch (filterMode) {
      case 'high': return 6;
      case 'medium': return 3;
      case 'low': return 0;
      default: return 0;
    }
  };

  const filteredItems = useMemo(() => {
    if (!portfolio?.items) return [];
    const threshold = getRiskThreshold();
    return portfolio.items.filter((item: any) => 
      getRiskScoreNumber(item.risk_score) >= threshold
    );
  }, [portfolio?.items, filterMode]);

  const portfolioTotalValue = useMemo(() => {
    return portfolio?.items?.reduce((sum: number, item: any) => 
      sum + normalizeAssetValue(item.asset_value), 0) || 1;
  }, [portfolio?.items]);

  const hazardBreakdown = useMemo(() => {
    const hazards: any = {};
    ALL_HAZARDS.forEach(hazard => {
      hazards[hazard] = {
        count: 0,
        value: 0,
        avgRisk: 0,
        totalAnnualLoss: 0,
        locations: []
      };
    });

    filteredItems.forEach((item: any) => {
      // For each hazard, check if this location has significant exposure (score > 3)
      ALL_HAZARDS.forEach(hazard => {
        const hazardScore = getRiskScoreNumber(item[hazard]);
        
        // Include location if it has meaningful exposure to this hazard
        if (hazardScore > 3) {
          const assetValue = normalizeAssetValue(item.asset_value);
          hazards[hazard].count++;
          hazards[hazard].value += assetValue;
          hazards[hazard].avgRisk += hazardScore;
          hazards[hazard].totalAnnualLoss += (assetValue * hazardScore) / 100;
          hazards[hazard].locations.push(item);
        }
      });
    });

    ALL_HAZARDS.forEach(hazard => {
      if (hazards[hazard].count > 0) {
        hazards[hazard].avgRisk = hazards[hazard].avgRisk / hazards[hazard].count;
      }
    });

    return hazards;
  }, [filteredItems]);

  const hazardChartData = useMemo(() => {
    return ALL_HAZARDS
      .map(hazard => ({
        name: HAZARD_LABELS[hazard],
        hazard,
        count: hazardBreakdown[hazard].count,
        assetValue: hazardBreakdown[hazard].value,  // Renamed to avoid Recharts conflict
        avgRisk: hazardBreakdown[hazard].avgRisk,
        totalAnnualLoss: hazardBreakdown[hazard].totalAnnualLoss,
        percentage: (hazardBreakdown[hazard].count / (filteredItems.length || 1)) * 100,
        portfolioPercentage: (hazardBreakdown[hazard].value / portfolioTotalValue) * 100,
        color: COLORS[hazard],
        x: hazardBreakdown[hazard].avgRisk,
        y: hazardBreakdown[hazard].value,
        z: hazardBreakdown[hazard].count,
        locations: hazardBreakdown[hazard].locations
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [hazardBreakdown, filteredItems.length, portfolioTotalValue]);

  const radarData = useMemo(() => {
    return ALL_HAZARDS
      .filter(hazard => hazardBreakdown[hazard].count > 0)
      .map(hazard => ({
        hazard: HAZARD_LABELS[hazard],
        risk: hazardBreakdown[hazard].avgRisk,
        fullMark: 10
      }));
  }, [hazardBreakdown]);

  const riskDistribution = useMemo(() => {
    const high = filteredItems.filter((item: any) => getRiskScoreNumber(item.risk_score) > 6).length;
    const medium = filteredItems.filter((item: any) => {
      const risk = getRiskScoreNumber(item.risk_score);
      return risk > 3 && risk <= 6;
    }).length;
    const low = filteredItems.filter((item: any) => getRiskScoreNumber(item.risk_score) <= 3).length;
    const total = filteredItems.length || 1;

    return [
      { name: 'High Risk', value: (high / total) * 100, count: high, color: COLORS.high },
      { name: 'Medium Risk', value: (medium / total) * 100, count: medium, color: COLORS.medium },
      { name: 'Low Risk', value: (low / total) * 100, count: low, color: COLORS.low }
    ].filter(item => item.count > 0);
  }, [filteredItems]);

  const handleBubbleClick = useCallback((data: any) => {
    if (data && data.payload) {
      setSelectedHazard(data.payload);
      setDetailDialogOpen(true);
    }
  }, []);

  const exportBreakdown = () => {
    const data = {
      summary: {
        totalLocations: filteredItems.length,
        totalValue: portfolioTotalValue,
        riskDistribution,
        hazardBreakdown: hazardChartData.map(h => ({
          hazard: h.name,
          propertiesAffected: h.count,
          valueAtRisk: h.assetValue,
          avgRiskScore: h.avgRisk.toFixed(2),
          estimatedAnnualLoss: h.totalAnnualLoss,
          percentOfPortfolio: h.portfolioPercentage.toFixed(2)
        })),
        filterSettings: { mode: filterMode }
      }
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `climate-risk-breakdown-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!portfolio || !portfolio.items || portfolio.items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">
          No portfolio data available. Please select or analyze a portfolio.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827' }}>
          Climate Risk Breakdown
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={filterMode}
            exclusive
            onChange={(_, newMode) => newMode && setFilterMode(newMode)}
            size="small"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="high" sx={{ color: COLORS.high }}>High</ToggleButton>
            <ToggleButton value="medium" sx={{ color: COLORS.medium }}>Medium</ToggleButton>
            <ToggleButton value="low" sx={{ color: COLORS.low }}>Low</ToggleButton>
          </ToggleButtonGroup>
          
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={(_, newType) => newType && setChartType(newType)}
            size="small"
          >
            <ToggleButton value="bubble">
              <Tooltip title="Interactive Bubble Chart">
                <BubbleChartIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="treemap">
              <Tooltip title="Treemap View">
                <TableChartIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Button
            startIcon={<DownloadIcon />}
            onClick={exportBreakdown}
            variant="outlined"
            size="small"
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#F0F9FF', border: '1px solid #BAE6FD' }}>
            <Typography variant="h4" fontWeight={700} color="primary">
              {filteredItems.length.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">Properties</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#FEF3C7', border: '1px solid #FCD34D' }}>
            <Typography variant="h4" fontWeight={700} color="#B45309">
              {hazardChartData.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">Risk Types</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#F0FDF4', border: '1px solid #86EFAC' }}>
            <Typography variant="h4" fontWeight={700} color="#15803D">
              {formatSmartValue(portfolioTotalValue)}
            </Typography>
            <Typography variant="body2" color="text.secondary">Total Value</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#FEF2F2', border: '1px solid #FECACA' }}>
            <Typography variant="h4" fontWeight={700} color="#DC2626">
              {formatSmartValue(hazardChartData.reduce((sum, h) => sum + h.totalAnnualLoss, 0))}
            </Typography>
            <Typography variant="body2" color="text.secondary">Est. Annual Loss</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Main Chart - Bubble or Treemap */}
        <Grid item xs={12} lg={8}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: '#111827', display: 'flex', alignItems: 'center', gap: 1 }}>
              {chartType === 'bubble' ? 'Risk vs Value Analysis' : 'Hazard Distribution'}
              <Tooltip title="Click on any hazard for detailed breakdown">
                <InfoIcon fontSize="small" color="action" />
              </Tooltip>
            </Typography>
            <Box sx={{ 
              backgroundColor: '#F9FAFB', 
              p: 2, 
              borderRadius: 2, 
              border: '1px solid #E5E7EB',
              position: 'relative'
            }}>
              {chartType === 'bubble' ? (
                <ResponsiveContainer width="100%" height={400}>
                  <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      type="number" 
                      dataKey="x" 
                      name="Risk Score" 
                      domain={[0, 10]}
                      tick={{ fill: '#6B7280' }}
                      label={{ value: 'Average Risk Score', position: 'bottom', offset: 40, fill: '#374151' }}
                    />
                    <YAxis 
                      type="number" 
                      dataKey="y" 
                      name="Value at Risk" 
                      tick={{ fill: '#6B7280' }}
                      tickFormatter={(val) => formatSmartValue(val)}
                      label={{ value: 'Value at Risk', angle: -90, position: 'insideLeft', offset: -60, fill: '#374151' }}
                    />
                    <ZAxis 
                      type="number" 
                      dataKey="z" 
                      range={[100, 2000]} 
                      name="Properties"
                    />
                    <RechartsTooltip content={<CustomBubbleTooltip />} />
                    <Scatter 
                      data={hazardChartData} 
                      onClick={handleBubbleClick}
                      cursor="pointer"
                    >
                      {hazardChartData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color}
                          fillOpacity={0.7}
                          stroke={entry.color}
                          strokeWidth={2}
                        />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <Treemap
                    data={hazardChartData}
                    dataKey="count"
                    aspectRatio={4 / 3}
                    stroke="#fff"
                    content={<CustomTreemapContent />}
                    onClick={handleBubbleClick}
                  />
                </ResponsiveContainer>
              )}
              
              {/* Legend */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mt: 2 }}>
                {hazardChartData.slice(0, 6).map((h, i) => (
                  <Chip 
                    key={i}
                    label={`${h.name} (${h.count})`}
                    size="small"
                    onClick={() => {
                      setSelectedHazard(h);
                      setDetailDialogOpen(true);
                    }}
                    sx={{ 
                      bgcolor: `${h.color}20`, 
                      borderColor: h.color,
                      borderWidth: 1,
                      borderStyle: 'solid',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: `${h.color}40` }
                    }}
                  />
                ))}
                {hazardChartData.length > 6 && (
                  <Chip 
                    label={`+${hazardChartData.length - 6} more`} 
                    size="small" 
                    variant="outlined"
                    onClick={() => setShowAllHazards(!showAllHazards)}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Risk Distribution Pie */}
        <Grid item xs={12} lg={4}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: '#111827' }}>
              Risk Distribution
            </Typography>
            <Box sx={{ backgroundColor: '#F9FAFB', p: 2, borderRadius: 2, border: '1px solid #E5E7EB' }}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={riskDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, count }) => `${name}: ${count}`}
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    paddingAngle={2}
                  >
                    {riskDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value: any, name: string, props: any) => [
                      `${props.payload.count} properties (${Number(value).toFixed(1)}%)`,
                      name
                    ]}
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              
              {/* Legend with counts */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                {riskDistribution.map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
                    <Typography variant="caption">
                      {item.name}: <strong>{item.count}</strong>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Radar Chart */}
        <Grid item xs={12}>
          <Box sx={{ backgroundColor: '#F9FAFB', p: 2, borderRadius: 2, border: '1px solid #E5E7EB' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#111827' }}>
              Risk Profile by Hazard Type
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis 
                  dataKey="hazard" 
                  tick={{ fill: '#6B7280', fontSize: 11 }}
                />
                <PolarRadiusAxis 
                  domain={[0, 10]} 
                  tick={{ fill: '#6B7280' }}
                />
                <Radar
                  name="Avg Risk"
                  dataKey="risk"
                  stroke="#2563EB"
                  fill="#2563EB"
                  fillOpacity={0.5}
                />
                <RechartsTooltip
                  formatter={(value: any) => [`${Number(value).toFixed(1)}/10`, 'Risk Score']}
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>

      {/* Hazard Summary Table */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#111827' }}>
          Hazard Summary
        </Typography>
        <TableContainer component={Paper} sx={{ border: '1px solid #E5E7EB' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                <TableCell sx={{ fontWeight: 600 }}>Hazard Type</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Properties Affected</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Value at Risk</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Risk Score</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Est. Annual Loss</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>% of Portfolio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hazardChartData.map((hazard) => (
                <TableRow 
                  key={hazard.hazard}
                  hover
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: `${hazard.color}10` } }}
                  onClick={() => {
                    setSelectedHazard(hazard);
                    setDetailDialogOpen(true);
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: hazard.color }} />
                      {hazard.name}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={hazard.count.toLocaleString()} 
                      size="small" 
                      sx={{ bgcolor: `${hazard.color}20`, fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="right">{formatSmartValue(hazard.assetValue)}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={hazard.avgRisk.toFixed(1)}
                      size="small"
                      color={hazard.avgRisk > 6 ? 'error' : hazard.avgRisk > 3 ? 'warning' : 'success'}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#DC2626', fontWeight: 500 }}>
                    {formatSmartValue(hazard.totalAnnualLoss)}
                  </TableCell>
                  <TableCell align="right">{hazard.portfolioPercentage.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedHazard && (
            <>
              <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: selectedHazard.color }} />
              {selectedHazard.name} - Affected Properties
            </>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedHazard && (
            <>
              {/* Summary Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#F9FAFB' }}>
                    <Typography variant="h5" fontWeight={700}>{selectedHazard.count}</Typography>
                    <Typography variant="caption" color="text.secondary">Properties</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#F9FAFB' }}>
                    <Typography variant="h5" fontWeight={700}>{formatSmartValue(selectedHazard.assetValue)}</Typography>
                    <Typography variant="caption" color="text.secondary">Value at Risk</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#F9FAFB' }}>
                    <Typography variant="h5" fontWeight={700}>{selectedHazard.avgRisk.toFixed(1)}</Typography>
                    <Typography variant="caption" color="text.secondary">Avg Risk</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#FEF2F2' }}>
                    <Typography variant="h5" fontWeight={700} color="error">{formatSmartValue(selectedHazard.totalAnnualLoss)}</Typography>
                    <Typography variant="caption" color="text.secondary">Annual Loss</Typography>
                  </Paper>
                </Grid>
              </Grid>
              
              {/* Properties List */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Affected Locations:</Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Location</TableCell>
                      <TableCell>City</TableCell>
                      <TableCell align="right">Risk Score</TableCell>
                      <TableCell align="right">Asset Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedHazard.locations?.slice(0, 50).map((loc: any, idx: number) => (
                      <TableRow key={idx} hover>
                        <TableCell>{loc.postal_code || loc.postalCode || '-'}</TableCell>
                        <TableCell>{loc.city ? `${loc.city}, ${loc.state}` : '-'}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={getRiskScoreNumber(loc.risk_score).toFixed(1)}
                            size="small"
                            color={getRiskScoreNumber(loc.risk_score) > 6 ? 'error' : getRiskScoreNumber(loc.risk_score) > 3 ? 'warning' : 'success'}
                          />
                        </TableCell>
                        <TableCell align="right">{formatSmartValue(normalizeAssetValue(loc.asset_value))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedHazard.locations?.length > 50 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Showing 50 of {selectedHazard.locations.length} affected properties
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* All Hazards Dialog */}
      <Dialog 
        open={showAllHazards} 
        onClose={() => setShowAllHazards(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600}>All Hazards Overview</Typography>
          <Typography variant="body2" color="text.secondary">
            {hazardChartData.length} hazards affecting your portfolio
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Hazard</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Locations</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Value at Risk</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Risk</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Est. Annual Loss</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>% of Portfolio</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hazardChartData.map((hazard, index) => (
                  <TableRow 
                    key={index}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { bgcolor: `${hazard.color}10` }
                    }}
                    onClick={() => {
                      setSelectedHazard(hazard);
                      setShowAllHazards(false);
                      setDetailDialogOpen(true);
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          bgcolor: hazard.color 
                        }} />
                        <Typography variant="body2" fontWeight={500}>
                          {hazard.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{hazard.count.toLocaleString()}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{formatSmartValue(hazard.assetValue)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={hazard.avgRisk.toFixed(1)}
                        size="small"
                        sx={{ 
                          bgcolor: hazard.avgRisk > 6 ? '#FEE2E2' : hazard.avgRisk > 3 ? '#FEF3C7' : '#D1FAE5',
                          color: hazard.avgRisk > 6 ? '#DC2626' : hazard.avgRisk > 3 ? '#D97706' : '#059669',
                          fontWeight: 600,
                          minWidth: 50
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="error.main" fontWeight={500}>
                        {formatSmartValue(hazard.totalAnnualLoss)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{hazard.portfolioPercentage.toFixed(1)}%</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Click on any row to see detailed breakdown for that hazard
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAllHazards(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}